import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import Layout from '../components/Layout';
import { useAuth } from '../contexts/AuthContext';
import { apiRequest } from '../utils/api';
import Modal from '../components/Modal';

interface ApiKey {
  id: number;
  keyName: string;
  apiKey: string; // Now always included from backend
  keyType: 'admin' | 'user';
  permissions: string[];
  maxProfiles: number;
  rateLimit: number;
  isActive: boolean;
  lastUsed: string | null;
  createdAt: string;
  expiresAt: string | null;
  deviceId: string | null;
  allowedDomain: string | null;
  _count: { jobs: number };
}

interface Profile {
  sessionId: string;
  phoneNumber: string;
  loginAt: string;
  proxy?: string;
  isLive: boolean;
  status: 'live' | 'dead';
}

interface ProfilesData {
  apiKeyId: number;
  apiKeyName: string;
  maxProfiles: number;
  stats: {
    total: number;
    live: number;
    dead: number;
    available: number;
  };
  profiles: Profile[];
}

const WhatsAppKeys: React.FC = () => {
  const { hasAnyPermission, hasPermission } = useAuth();
  const navigate = useNavigate();
  const [apiKeys, setApiKeys] = useState<ApiKey[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [editingKey, setEditingKey] = useState<ApiKey | null>(null);
  const [newApiKey, setNewApiKey] = useState<string>('');
  const [visibleKeys, setVisibleKeys] = useState<Set<number>>(new Set());

  // Profile management state
  const [showProfilesModal, setShowProfilesModal] = useState(false);
  const [selectedKeyForProfiles, setSelectedKeyForProfiles] = useState<ApiKey | null>(null);
  const [profilesData, setProfilesData] = useState<ProfilesData | null>(null);
  const [loadingProfiles, setLoadingProfiles] = useState(false);

  // Form state
  const [formData, setFormData] = useState({
    keyName: '',
    keyType: 'user' as 'admin' | 'user',
    permissions: [] as string[],
    maxProfiles: 1,
    rateLimit: 1000,
    expiryDays: 30,
    deviceId: '',
    allowedDomain: ''
  });

  const [notification, setNotification] = useState<{
    show: boolean;
    type: 'success' | 'error';
    message: string;
  }>({ show: false, type: 'success', message: '' });

  const availablePermissions = [
    { value: 'login', label: '🔑 Đăng nhập WhatsApp', color: '#059669' },
    { value: 'send_message', label: '📤 Gửi tin nhắn', color: '#3b82f6' },
    { value: 'reply_message', label: '↩️ Reply tin nhắn', color: '#06b6d4' },
    { value: 'get_groups', label: '👥 Lấy danh sách nhóm', color: '#10b981' },
    { value: 'get_contacts', label: '📞 Lấy danh sách liên hệ', color: '#f59e0b' },
    { value: 'get_chat_history', label: '💬 Lấy lịch sử chat', color: '#ef4444' },
    { value: 'update_application', label: '🔄 Cập nhật ứng dụng', color: '#ec4899' },
    { value: 'check_status', label: '🔍 Kiểm tra trạng thái key', color: '#8b5cf6' }
  ];

  const fetchApiKeys = async () => {
    try {
      setLoading(true);
      setError('');
      
      const response = await apiRequest('/api/whatsapp-management/keys');
      
      if (response.ok) {
        const data = await response.json();
        setApiKeys(data.data || []);
      } else {
        const errorData = await response.json();
        setError(errorData.error || 'Không thể tải danh sách API keys');
      }
    } catch (error) {
      setError('Lỗi kết nối server');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchApiKeys();
  }, []);

  const showNotification = (type: 'success' | 'error', message: string) => {
    setNotification({ show: true, type, message });
    setTimeout(() => {
      setNotification({ show: false, type: 'success', message: '' });
    }, 5000);
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!formData.keyName.trim()) {
      showNotification('error', 'Tên key là bắt buộc');
      return;
    }

    if (formData.permissions.length === 0) {
      showNotification('error', 'Phải chọn ít nhất một quyền');
      return;
    }

    if (formData.maxProfiles !== -1 && (formData.maxProfiles < 1 || formData.maxProfiles > 10)) {
      showNotification('error', 'Max Profiles phải là No limit hoặc từ 1 đến 10');
      return;
    }

    if (formData.keyType === 'user' && formData.rateLimit !== -1 && (formData.rateLimit < 1 || formData.rateLimit > 1000000000)) {
      showNotification('error', 'Rate Limit must be No limit or 1-1000000000');
      return;
    }

    if (formData.keyType === 'user' && formData.expiryDays !== 0 && (formData.expiryDays < 1 || formData.expiryDays > 365)) {
      showNotification('error', 'Thời hạn phải là Không thời hạn hoặc từ 1 đến 365 ngày');
      return;
    }

    try {
      const url = editingKey ? `/api/whatsapp-management/keys/${editingKey.id}` : '/api/whatsapp-management/keys';
      const method = editingKey ? 'PUT' : 'POST';
      
      // Prepare data - exclude expiryDays for admin keys
      const requestData = {
        ...formData,
        ...(formData.keyType === 'admin' && { expiryDays: undefined }),
        // Always include deviceId and allowedDomain (can be empty string to clear them)
        deviceId: formData.deviceId.trim() || '',
        allowedDomain: formData.allowedDomain
          .split(/[,\n]/)
          .map(d => d.trim())
          .filter(d => d.length > 0)
          .join(',')
      };
      
      const response = await apiRequest(url, {
        method,
        body: JSON.stringify(requestData)
      });

      if (response.ok) {
        const data = await response.json();
        
        if (!editingKey && data.data.apiKey) {
          setNewApiKey(data.data.apiKey);
        }
        
        await fetchApiKeys();
        resetForm();
        setShowCreateModal(false);
        showNotification('success', editingKey ? 'Cập nhật API key thành công' : 'Tạo API key thành công');
      } else {
        const errorData = await response.json();
        let errorMessage = errorData.error || 'Có lỗi xảy ra';

        // Handle validation errors with details
        if (errorData.details && Array.isArray(errorData.details)) {
          errorMessage = errorData.details.map((detail: any) => detail.msg).join(', ');
        }

        showNotification('error', errorMessage);
      }
    } catch (error) {
      showNotification('error', 'Lỗi kết nối server');
    }
  };

  const handleDelete = async (keyId: number, keyName: string) => {
    if (!window.confirm(`Bạn có chắc chắn muốn xóa API key "${keyName}"?`)) {
      return;
    }

    try {
      const response = await apiRequest(`/api/whatsapp-management/keys/${keyId}`, {
        method: 'DELETE'
      });

      if (response.ok) {
        await fetchApiKeys();
        showNotification('success', 'Xóa API key thành công');
      } else {
        const errorData = await response.json();
        showNotification('error', errorData.error || 'Có lỗi xảy ra');
      }
    } catch (error) {
      showNotification('error', 'Lỗi kết nối server');
    }
  };

  const handleClearSessions = async (keyId: number, keyName: string) => {
    if (!window.confirm(`Bạn có chắc chắn muốn xóa tất cả sessions của API key "${keyName}"?`)) {
      return;
    }

    try {
      const response = await apiRequest(`/api/whatsapp-management/keys/${keyId}/sessions/clear`, {
        method: 'DELETE'
      });

      if (response.ok) {
        const data = await response.json();
        showNotification('success', `Đã xóa ${data.data.clearedSessions} sessions chưa login của API key "${keyName}"`);
      } else {
        const errorData = await response.json();
        showNotification('error', errorData.error || 'Có lỗi xảy ra');
      }
    } catch (error) {
      showNotification('error', 'Lỗi kết nối server');
    }
  };

  const handleEdit = (key: ApiKey) => {
    setEditingKey(key);
    
    // Calculate expiryDays from expiresAt
    let expiryDays = 0; // no expiry by default when key has no expiresAt
    if (key.expiresAt) {
      const expiresDate = new Date(key.expiresAt);
      const now = new Date();
      const diffTime = expiresDate.getTime() - now.getTime();
      const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
      expiryDays = Math.max(1, diffDays); // At least 1 day
    }
    
    setFormData({
      keyName: key.keyName,
      keyType: key.keyType || 'user', // Add keyType with fallback
      permissions: key.permissions,
      maxProfiles: key.maxProfiles ?? 1,
      rateLimit: key.rateLimit,
      expiryDays: expiryDays,
      deviceId: key.deviceId || '',
      allowedDomain: key.allowedDomain ? key.allowedDomain.split(',').join('\n') : ''
    });
    setShowCreateModal(true);
  };

  const resetForm = () => {
    setFormData({
      keyName: '',
      keyType: 'user',
      permissions: [],
      maxProfiles: 1,
      rateLimit: 1000,
      expiryDays: 30,
      deviceId: '',
      allowedDomain: ''
    });
    setEditingKey(null);
    setNewApiKey('');
  };

  const handlePermissionChange = (permission: string) => {
    setFormData(prev => ({
      ...prev,
      permissions: prev.permissions.includes(permission)
        ? prev.permissions.filter(p => p !== permission)
        : [...prev.permissions, permission]
    }));
  };

  const copyToClipboard = (text: string) => {
    navigator.clipboard.writeText(text);
    showNotification('success', 'Đã copy vào clipboard');
  };

  const handleClearCache = async (keyId: number, keyName: string) => {
    if (!window.confirm(`Xóa cache cho API key "${keyName}"?\n\nCache sẽ được làm mới ngay lập tức.`)) {
      return;
    }

    try {
      const response = await apiRequest(`/api/whatsapp-management/keys/${keyId}/cache`, {
        method: 'DELETE'
      });

      if (response.ok) {
        showNotification('success', 'Đã xóa cache thành công');
      } else {
        const errorData = await response.json();
        showNotification('error', errorData.error || 'Có lỗi xảy ra');
      }
    } catch (error) {
      showNotification('error', 'Lỗi kết nối server');
    }
  };

  // Profile management functions
  const handleViewProfiles = async (apiKey: ApiKey) => {
    setSelectedKeyForProfiles(apiKey);
    setLoadingProfiles(true);
    setShowProfilesModal(true);

    try {
      const response = await apiRequest(`/api/whatsapp-management/keys/${apiKey.id}/profiles`);
      const result = await response.json();
      if (result.success) {
        setProfilesData(result.data);
      } else {
        throw new Error(result.error || 'Không thể tải profiles');
      }
    } catch (err: any) {
      setError(err.message || 'Không thể tải danh sách profiles');
      setShowProfilesModal(false);
    } finally {
      setLoadingProfiles(false);
    }
  };

  const handleUpdateProfileLive = async (sessionId: string, isLive: boolean) => {
    if (!selectedKeyForProfiles) return;

    try {
      await apiRequest(`/api/whatsapp-management/keys/${selectedKeyForProfiles.id}/profiles/${sessionId}/live`, {
        method: 'PUT',
        body: JSON.stringify({ isLive })
      });

      showNotification('success', `Profile đã được ${isLive ? 'kích hoạt' : 'vô hiệu hóa'}`);

      // Refresh profiles data
      await handleViewProfiles(selectedKeyForProfiles);
    } catch (err: any) {
      showNotification('error', err.message || 'Không thể cập nhật trạng thái profile');
    }
  };

  const handleDeleteProfile = async (sessionId: string, phoneNumber: string) => {
    if (!selectedKeyForProfiles) return;

    // Confirm deletion
    if (!window.confirm(`Bạn có chắc chắn muốn xóa profile ${phoneNumber} (${sessionId})?`)) {
      return;
    }

    try {
      await apiRequest(`/api/whatsapp-management/keys/${selectedKeyForProfiles.id}/profiles/${sessionId}`, {
        method: 'DELETE'
      });

      showNotification('success', `Profile ${phoneNumber} đã được xóa thành công`);

      // Refresh profiles data
      await handleViewProfiles(selectedKeyForProfiles);
    } catch (err: any) {
      showNotification('error', err.message || 'Không thể xóa profile');
    }
  };

  const toggleKeyVisibility = (keyId: number) => {
    setVisibleKeys(prev => {
      const newSet = new Set(prev);
      if (newSet.has(keyId)) {
        newSet.delete(keyId);
      } else {
        newSet.add(keyId);
      }
      return newSet;
    });
  };

  const formatDate = (dateString: string) => {
    const dt = new Date(dateString);
    const pad = (n: number) => String(n).padStart(2, '0');
    const d = pad(dt.getUTCDate());
    const m = pad(dt.getUTCMonth() + 1);
    const y = dt.getUTCFullYear();
    const hh = pad(dt.getUTCHours());
    const mm = pad(dt.getUTCMinutes());
    const ss = pad(dt.getUTCSeconds());
    return `${d}/${m}/${y} ${hh}:${mm}:${ss}`;
  };

  // Check permissions
  if (!hasAnyPermission('whatsapp')) {
    return (
      <Layout currentPage="whatsapp">
        <div style={{ 
          display: 'flex', 
          alignItems: 'center', 
          justifyContent: 'center', 
          height: '400px',
          flexDirection: 'column',
          gap: '16px'
        }}>
          <div style={{ fontSize: '48px' }}>🔒</div>
          <div style={{ 
            fontSize: '18px', 
            fontWeight: '600', 
            color: '#374151',
            textAlign: 'center'
          }}>
            Không có quyền truy cập
          </div>
          <div style={{ 
            fontSize: '14px', 
            color: '#6b7280',
            textAlign: 'center'
          }}>
            Bạn không có quyền truy cập module WhatsApp API. Vui lòng liên hệ quản trị viên.
          </div>
        </div>
      </Layout>
    );
  }

  if (loading) {
    return (
      <Layout currentPage="whatsapp">
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', height: '400px' }}>
          <div className="loading-spinner" style={{ width: '40px', height: '40px' }}></div>
        </div>
      </Layout>
    );
  }

  return (
    <Layout currentPage="whatsapp">
      <div style={{ width: '100%', maxWidth: '100%' }}>
        {/* Notification */}
        {notification.show && (
          <div style={{
            position: 'fixed',
            top: '20px',
            right: '20px',
            zIndex: 1000,
            padding: '12px 16px',
            borderRadius: '8px',
            backgroundColor: notification.type === 'success' ? '#dcfce7' : '#fecaca',
            border: `1px solid ${notification.type === 'success' ? '#16a34a' : '#dc2626'}`,
            color: notification.type === 'success' ? '#15803d' : '#dc2626',
            fontSize: '14px',
            fontWeight: '500',
            boxShadow: '0 4px 12px rgba(0,0,0,0.15)'
          }}>
            {notification.message}
          </div>
        )}

        {/* Header */}
        <div style={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          marginBottom: '24px',
          flexWrap: 'wrap',
          gap: '16px'
        }}>
          <div>
            <h1 style={{ 
              fontSize: '24px', 
              fontWeight: '700', 
              color: '#1e293b', 
              margin: '0 0 8px 0' 
            }}>
              WhatsApp API Keys
            </h1>
            <p style={{ 
              fontSize: '14px', 
              color: '#64748b', 
              margin: 0 
            }}>
              Quản lý API keys cho WhatsApp Bot service
            </p>
          </div>
          
          <div style={{ display: 'flex', gap: '12px' }}>
            
            <button
              onClick={() => navigate('/whatsapp/docs')}
              style={{
                padding: '8px 16px',
                backgroundColor: '#f8fafc',
                color: '#475569',
                border: '1px solid #e2e8f0',
                borderRadius: '6px',
                fontSize: '14px',
                fontWeight: '500',
                cursor: 'pointer',
                display: 'flex',
                alignItems: 'center',
                gap: '8px',
                transition: 'all 0.2s'
              }}
              onMouseEnter={(e) => {
                e.currentTarget.style.backgroundColor = '#f1f5f9';
                e.currentTarget.style.borderColor = '#cbd5e1';
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.backgroundColor = '#f8fafc';
                e.currentTarget.style.borderColor = '#e2e8f0';
              }}
            >
              📚 API Documentation
            </button>
            
            {hasPermission('whatsapp', 'create') && (
              <button
                onClick={() => {
                  resetForm();
                  setShowCreateModal(true);
                }}
                style={{
                padding: '10px 16px',
                backgroundColor: '#2563eb',
                color: 'white',
                border: 'none',
                borderRadius: '8px',
                fontSize: '14px',
                fontWeight: '500',
                cursor: 'pointer',
                display: 'flex',
                alignItems: 'center',
                gap: '8px'
              }}
            >
              ➕ Tạo API Key
            </button>
            )}
          </div>
        </div>

        {error && (
          <div style={{
            backgroundColor: '#fecaca',
            border: '1px solid #f87171',
            color: '#dc2626',
            padding: '12px 16px',
            borderRadius: '8px',
            marginBottom: '24px',
            fontSize: '14px'
          }}>
            {error}
          </div>
        )}

        {/* API Keys Table */}
        <div style={{
          backgroundColor: 'white',
          borderRadius: '12px',
          boxShadow: '0 1px 3px rgba(0,0,0,0.1)',
          border: '1px solid #f1f5f9',
          overflow: 'hidden'
        }}>
          <div style={{ overflowX: 'auto' }}>
            <table style={{ width: '100%', borderCollapse: 'collapse' }}>
              <thead>
                <tr style={{ backgroundColor: '#f8fafc' }}>
                  <th style={{ padding: '12px', textAlign: 'left', fontSize: '12px', fontWeight: '600', color: '#475569' }}>Tên Key</th>
                  <th style={{ padding: '12px', textAlign: 'left', fontSize: '12px', fontWeight: '600', color: '#475569' }}>Loại</th>
                  <th style={{ padding: '12px', textAlign: 'left', fontSize: '12px', fontWeight: '600', color: '#475569' }}>Xác thực</th>
                  <th style={{ padding: '12px', textAlign: 'left', fontSize: '12px', fontWeight: '600', color: '#475569' }}>API Key</th>
                  <th style={{ padding: '12px', textAlign: 'left', fontSize: '12px', fontWeight: '600', color: '#475569' }}>Quyền</th>
                  <th style={{ padding: '12px', textAlign: 'left', fontSize: '12px', fontWeight: '600', color: '#475569' }}>Rate Limit</th>
                  <th style={{ padding: '12px', textAlign: 'left', fontSize: '12px', fontWeight: '600', color: '#475569' }}>Trạng thái</th>
                  <th style={{ padding: '12px', textAlign: 'left', fontSize: '12px', fontWeight: '600', color: '#475569' }}>Jobs</th>
                  <th style={{ padding: '12px', textAlign: 'left', fontSize: '12px', fontWeight: '600', color: '#475569' }}>Sử dụng cuối</th>
                  <th style={{ padding: '12px', textAlign: 'left', fontSize: '12px', fontWeight: '600', color: '#475569' }}>Thao tác</th>
                </tr>
              </thead>
              <tbody>
                {apiKeys.map((key, index) => (
                  <tr key={key.id} style={{
                    borderBottom: index === apiKeys.length - 1 ? 'none' : '1px solid #f1f5f9'
                  }}>
                    <td style={{ padding: '12px', fontSize: '14px', fontWeight: '600', color: '#1e293b' }}>
                      {key.keyName}
                    </td>
                    <td style={{ padding: '12px', fontSize: '12px' }}>
                      <span style={{
                        padding: '4px 8px',
                        borderRadius: '12px',
                        fontSize: '11px',
                        fontWeight: '600',
                        backgroundColor: key.keyType === 'admin' ? '#fee2e2' : '#f0f9ff',
                        color: key.keyType === 'admin' ? '#dc2626' : '#0369a1'
                      }}>
                        {key.keyType === 'admin' ? '👑 Admin' : '👤 User'}
                      </span>
                    </td>
                    <td style={{ padding: '12px', fontSize: '12px' }}>
                      <div style={{ display: 'flex', flexDirection: 'column', gap: '4px' }}>
                        {key.deviceId && (
                          <div style={{
                            padding: '2px 6px',
                            borderRadius: '8px',
                            fontSize: '10px',
                            backgroundColor: '#f0f9ff',
                            color: '#0369a1',
                            border: '1px solid #0ea5e9'
                          }}>
                            🔑 {key.deviceId.substring(0, 12)}...
                          </div>
                        )}
                        {key.allowedDomain && (
                          <div style={{
                            padding: '2px 6px',
                            borderRadius: '8px',
                            fontSize: '10px',
                            backgroundColor: '#f0fdf4',
                            color: '#15803d',
                            border: '1px solid #22c55e',
                            maxWidth: '150px'
                          }}>
                            🌐 {key.allowedDomain.includes(',') 
                              ? `${key.allowedDomain.split(',')[0].trim()}... (+${key.allowedDomain.split(',').length - 1})`
                              : key.allowedDomain
                            }
                          </div>
                        )}
                        {!key.deviceId && !key.allowedDomain && (
                          <span style={{ fontSize: '10px', color: '#6b7280' }}>
                            Chỉ API Key
                          </span>
                        )}
                      </div>
                    </td>
                    <td style={{ padding: '12px', fontSize: '12px', color: '#475569', fontFamily: 'monospace' }}>
                      <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                        <span style={{ fontFamily: 'monospace', fontSize: '11px' }}>
                          {key.apiKey ? (
                            visibleKeys.has(key.id) ? key.apiKey : key.apiKey.substring(0, 8) + '••••••••••••••••••••'
                          ) : '••••••••••••••••••••'}
                        </span>

                        {key.apiKey && (
                          <div style={{ display: 'flex', gap: '4px' }}>
                            <button
                              onClick={() => toggleKeyVisibility(key.id)}
                              style={{
                                padding: '2px 6px',
                                backgroundColor: '#f8fafc',
                                border: '1px solid #e2e8f0',
                                borderRadius: '4px',
                                fontSize: '10px',
                                cursor: 'pointer',
                                color: '#64748b'
                              }}
                              title={visibleKeys.has(key.id) ? "Hide API key" : "Show API key"}
                            >
                              {visibleKeys.has(key.id) ? 'Hide' : 'Show'}
                            </button>
                            <button
                              onClick={() => copyToClipboard(key.apiKey!)}
                              style={{
                                padding: '2px 6px',
                                backgroundColor: '#f1f5f9',
                                border: '1px solid #e2e8f0',
                                borderRadius: '4px',
                                fontSize: '10px',
                                cursor: 'pointer',
                                color: '#475569'
                              }}
                              title="Copy API key"
                            >
                              Copy
                            </button>
                          </div>
                        )}
                      </div>
                    </td>
                    <td style={{ padding: '12px', fontSize: '12px', color: '#475569' }}>
                      <div style={{ display: 'flex', flexWrap: 'wrap', gap: '4px' }}>
                        {key.permissions.slice(0, 3).map(perm => {
                          const permConfig = availablePermissions.find(p => p.value === perm);
                          return (
                            <span
                              key={perm}
                              style={{
                                padding: '2px 6px',
                                backgroundColor: permConfig?.color || '#6b7280',
                                color: 'white',
                                borderRadius: '4px',
                                fontSize: '10px',
                                fontWeight: '500'
                              }}
                            >
                              {permConfig?.label.split(' ')[0] || perm}
                            </span>
                          );
                        })}
                        {key.permissions.length > 3 && (
                          <span style={{ fontSize: '10px', color: '#6b7280' }}>
                            +{key.permissions.length - 3} more
                          </span>
                        )}
                      </div>
                    </td>
                    <td style={{ padding: '12px', fontSize: '14px', color: '#475569' }}>
                      {key.rateLimit === -1 ? (
                        <span style={{ color: '#059669', fontWeight: '500' }}>Vô hạn</span>
                      ) : (
                        `${(key.rateLimit || 0).toLocaleString()}/h`
                      )}
                    </td>
                    <td style={{ padding: '12px' }}>
                      <span style={{
                        padding: '4px 8px',
                        borderRadius: '12px',
                        fontSize: '12px',
                        fontWeight: '500',
                        backgroundColor: key.isActive ? '#dcfce7' : '#fecaca',
                        color: key.isActive ? '#16a34a' : '#dc2626'
                      }}>
                        {key.isActive ? 'Hoạt động' : 'Tạm dừng'}
                      </span>
                    </td>
                    <td style={{ padding: '12px', fontSize: '14px', color: '#475569' }}>
                      {(key._count?.jobs || 0).toLocaleString()}
                    </td>
                    <td style={{ padding: '12px', fontSize: '13px', color: '#64748b' }}>
                      {key.lastUsed ? formatDate(key.lastUsed) : 'Chưa sử dụng'}
                    </td>
                    <td style={{ padding: '12px', textAlign: 'center' }}>
                      <div style={{ display: 'flex', gap: '4px', justifyContent: 'center' }}>
                        {hasPermission('whatsapp', 'read') && (
                          <button
                            onClick={() => handleViewProfiles(key)}
                            style={{
                              padding: '4px 8px',
                              backgroundColor: '#f0fdf4',
                              border: '1px solid #86efac',
                              borderRadius: '4px',
                              fontSize: '12px',
                              color: '#16a34a',
                              cursor: 'pointer',
                              marginRight: '4px'
                            }}
                            title="Xem Profiles"
                          >
                            👥
                          </button>
                        )}
                        {hasPermission('whatsapp', 'update') && (
                          <button
                            onClick={() => handleEdit(key)}
                            style={{
                              padding: '4px 8px',
                              backgroundColor: '#dbeafe',
                              border: '1px solid #93c5fd',
                              borderRadius: '4px',
                              fontSize: '12px',
                              color: '#1d4ed8',
                              cursor: 'pointer'
                            }}
                          >
                            ✏️
                          </button>
                        )}
                        {hasPermission('whatsapp', 'update') && (
                          <button
                            onClick={() => handleClearCache(key.id, key.keyName)}
                            style={{
                              padding: '4px 8px',
                              backgroundColor: '#e0e7ff',
                              border: '1px solid #a5b4fc',
                              borderRadius: '4px',
                              fontSize: '12px',
                              color: '#4338ca',
                              cursor: 'pointer',
                              marginRight: '4px'
                            }}
                            title="Clear Cache"
                          >
                            🔄
                          </button>
                        )}
                        {hasPermission('whatsapp', 'delete') && (
                          <>
                            <button
                              onClick={() => handleClearSessions(key.id, key.keyName)}
                              style={{
                                padding: '4px 8px',
                                backgroundColor: '#fef3c7',
                                border: '1px solid #fbbf24',
                                borderRadius: '4px',
                                fontSize: '12px',
                                color: '#d97706',
                                cursor: 'pointer',
                                marginRight: '4px'
                              }}
                              title="Clear Sessions"
                            >
                              🧹
                            </button>
                            <button
                              onClick={() => handleDelete(key.id, key.keyName)}
                              style={{
                                padding: '4px 8px',
                                backgroundColor: '#fecaca',
                                border: '1px solid #fca5a5',
                                borderRadius: '4px',
                                fontSize: '12px',
                                color: '#dc2626',
                                cursor: 'pointer'
                              }}
                            >
                              🗑️
                            </button>
                          </>
                        )}
                      </div>
                    </td>
                  </tr>
                ))}
                {apiKeys.length === 0 && (
                  <tr>
                    <td colSpan={8} style={{ 
                      padding: '40px', 
                      textAlign: 'center', 
                      color: '#94a3b8',
                      fontSize: '14px'
                    }}>
                      Chưa có API key nào
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        </div>

        {/* Create/Edit Modal */}
        <Modal
          isOpen={showCreateModal}
          onClose={() => {
            setShowCreateModal(false);
            resetForm();
          }}
          title={editingKey ? 'Chỉnh sửa API Key' : 'Tạo API Key mới'}
          size="lg"
        >
          <form onSubmit={handleSubmit}>
            <div style={{ marginBottom: '16px' }}>
              <label style={{
                display: 'block',
                fontSize: '14px',
                fontWeight: '500',
                color: '#374151',
                marginBottom: '6px'
              }}>
                Tên Key *
              </label>
              <input
                type="text"
                value={formData.keyName}
                onChange={(e) => setFormData({ ...formData, keyName: e.target.value })}
                placeholder="Ví dụ: Production API, Test Key..."
                style={{
                  width: '100%',
                  padding: '10px 12px',
                  border: '1px solid #d1d5db',
                  borderRadius: '8px',
                  fontSize: '14px'
                }}
                required
              />
            </div>

            {/* Key Type */}
            <div style={{ marginBottom: '16px' }}>
              <label style={{
                display: 'block',
                fontSize: '14px',
                fontWeight: '500',
                color: '#374151',
                marginBottom: '6px'
              }}>
                Loại Key *
              </label>
              <select
                value={formData.keyType}
                onChange={(e) => {
                  const newKeyType = e.target.value as 'admin' | 'user';
                  setFormData({ 
                    ...formData, 
                    keyType: newKeyType,
                    // Admin keys get all permissions and unlimited expiry
                    permissions: newKeyType === 'admin' ? availablePermissions.map(p => p.value) : formData.permissions,
                    expiryDays: newKeyType === 'admin' ? 0 : formData.expiryDays
                  });
                }}
                style={{
                  width: '100%',
                  padding: '10px 12px',
                  border: '1px solid #d1d5db',
                  borderRadius: '8px',
                  fontSize: '14px',
                  backgroundColor: 'white'
                }}
              >
                <option value="user">👤 User Key (Chỉ tạo jobs)</option>
                <option value="admin">🔧 Admin Key (Toàn quyền)</option>
              </select>
            </div>

            {formData.keyType === 'user' && (
            <div style={{ marginBottom: '16px' }}>
              <label style={{
                display: 'block',
                fontSize: '14px',
                fontWeight: '500',
                color: '#374151',
                marginBottom: '6px'
              }}>
                Quyền truy cập *
              </label>
              <div style={{
                display: 'grid',
                gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))',
                gap: '8px',
                padding: '12px',
                border: '1px solid #d1d5db',
                borderRadius: '8px',
                backgroundColor: '#f9fafb'
              }}>
                {availablePermissions.map(perm => (
                  <label key={perm.value} style={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: '8px',
                    cursor: 'pointer',
                    padding: '4px'
                  }}>
                    <input
                      type="checkbox"
                      checked={formData.permissions.includes(perm.value)}
                      onChange={() => handlePermissionChange(perm.value)}
                      style={{ margin: 0 }}
                    />
                    <span style={{ fontSize: '13px', color: '#374151' }}>
                      {perm.label}
                    </span>
                  </label>
                ))}
              </div>
            </div>
            )}

            <div style={{ display: 'flex', gap: '40px', marginBottom: '32px', paddingRight: '16px' }}>
              {formData.keyType === 'user' && (
              <div style={{ flex: '1' }}>
                <label style={{
                  display: 'block',
                  fontSize: '14px',
                  fontWeight: '500',
                  color: '#374151',
                  marginBottom: '12px'
                }}>
                  Rate Limit (requests/hour)
                </label>
                <input
                  type="number"
                  value={formData.rateLimit === -1 ? '' : formData.rateLimit}
                  onChange={(e) => setFormData({ ...formData, rateLimit: parseInt(e.target.value) || 1000 })}
                  min="1"
                  max="1000000000"
                  disabled={formData.rateLimit === -1}
                  placeholder={formData.rateLimit === -1 ? 'No limit' : '1000'}
                  style={{
                    width: '100%',
                    padding: '12px',
                    border: '1px solid #d1d5db',
                    borderRadius: '8px',
                    fontSize: '14px',
                    backgroundColor: formData.rateLimit === -1 ? '#f8fafc' : '#ffffff'
                  }}
                />
                <label style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: '8px',
                  marginTop: '10px',
                  fontSize: '13px',
                  color: '#374151',
                  cursor: 'pointer'
                }}>
                  <input
                    type="checkbox"
                    checked={formData.rateLimit === -1}
                    onChange={(e) => setFormData({ ...formData, rateLimit: e.target.checked ? -1 : 1000 })}
                  />
                  <span>No limit</span>
                </label>
              </div>
              )}
              
              <div style={{ flex: '1' }}>
                <label style={{
                  display: 'block',
                  fontSize: '14px',
                  fontWeight: '500',
                  color: '#374151',
                  marginBottom: '12px'
                }}>
                  Max Profiles *
                </label>
                <input
                  type="number"
                  value={formData.maxProfiles === -1 ? '' : formData.maxProfiles}
                  onChange={(e) => setFormData({ ...formData, maxProfiles: parseInt(e.target.value) || 1 })}
                  min="1"
                  max="10"
                  disabled={formData.maxProfiles === -1}
                  placeholder={formData.maxProfiles === -1 ? 'No limit' : '1'}
                  style={{
                    width: '100%',
                    padding: '12px',
                    border: '1px solid #d1d5db',
                    borderRadius: '8px',
                    fontSize: '14px',
                    backgroundColor: formData.maxProfiles === -1 ? '#f8fafc' : '#ffffff'
                  }}
                />
                <label style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: '8px',
                  marginTop: '10px',
                  fontSize: '13px',
                  color: '#374151',
                  cursor: 'pointer'
                }}>
                  <input
                    type="checkbox"
                    checked={formData.maxProfiles === -1}
                    onChange={(e) => setFormData({ ...formData, maxProfiles: e.target.checked ? -1 : 1 })}
                  />
                  <span>No limit</span>
                </label>
                <div style={{ fontSize: '12px', color: '#6b7280', lineHeight: '1.4', marginTop: '6px' }}>
                  Số lượng WhatsApp profiles tối đa cho API key này
                </div>
              </div>

              {formData.keyType === 'user' && (
              <div style={{ flex: '1' }}>
                <label style={{
                  display: 'block',
                  fontSize: '14px',
                  fontWeight: '500',
                  color: '#374151',
                  marginBottom: '12px'
                }}>
                  Thời hạn (ngày)
                </label>
                <input
                  type="number"
                  min="1"
                  max="365"
                  value={formData.expiryDays === 0 ? '' : formData.expiryDays}
                  onChange={(e) => setFormData({ ...formData, expiryDays: parseInt(e.target.value) || 30 })}
                  placeholder={formData.expiryDays === 0 ? 'Không thời hạn' : '30'}
                  disabled={formData.expiryDays === 0}
                  style={{
                    width: '100%',
                    padding: '12px',
                    border: '1px solid #d1d5db',
                    borderRadius: '8px',
                    fontSize: '14px',
                    marginBottom: '8px',
                    backgroundColor: formData.expiryDays === 0 ? '#f8fafc' : '#ffffff'
                  }}
                />
                <label style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: '8px',
                  marginBottom: '8px',
                  fontSize: '13px',
                  color: '#374151',
                  cursor: 'pointer'
                }}>
                  <input
                    type="checkbox"
                    checked={formData.expiryDays === 0}
                    onChange={(e) => setFormData({ ...formData, expiryDays: e.target.checked ? 0 : 30 })}
                  />
                  <span>Không thời hạn</span>
                </label>
                <div style={{ fontSize: '12px', color: '#6b7280', lineHeight: '1.4', marginTop: '6px' }}>
                  Ví dụ: 0 (không thời hạn), 30 (30 ngày), 365 (1 năm)
                </div>
              </div>
              )}
            </div>

            {/* Device ID and Domain fields - only for user keys */}
            {formData.keyType === 'user' && (
            <div style={{ marginBottom: '24px' }}>
              <h4 style={{
                fontSize: '14px',
                fontWeight: '600',
                color: '#374151',
                marginBottom: '16px'
              }}>
                🔐 Xác thực bổ sung (tùy chọn)
              </h4>
              <div style={{ display: 'flex', gap: '40px', paddingRight: '16px' }}>
                <div style={{ flex: '1' }}>
                  <label style={{
                    display: 'block',
                    fontSize: '14px',
                    fontWeight: '500',
                    color: '#374151',
                    marginBottom: '12px'
                  }}>
                    Device ID
                  </label>
                  <input
                    type="text"
                    value={formData.deviceId}
                    onChange={(e) => setFormData({ ...formData, deviceId: e.target.value })}
                    placeholder="device_123456"
                    style={{
                      width: '100%',
                      padding: '12px',
                      border: '1px solid #d1d5db',
                      borderRadius: '8px',
                      fontSize: '14px',
                      marginBottom: '8px'
                    }}
                  />
                  <div style={{ fontSize: '12px', color: '#6b7280', lineHeight: '1.4', marginTop: '6px' }}>
                    Yêu cầu header X-Device-ID khi gọi API
                  </div>
                </div>
                
                <div style={{ flex: '1' }}>
                  <label style={{
                    display: 'block',
                    fontSize: '14px',
                    fontWeight: '500',
                    color: '#374151',
                    marginBottom: '12px'
                  }}>
                    Allowed Domains
                  </label>
                  <textarea
                    value={formData.allowedDomain}
                    onChange={(e) => setFormData({ ...formData, allowedDomain: e.target.value })}
                    placeholder="example.com&#10;api.example.com&#10;subdomain.example.com"
                    rows={3}
                    style={{
                      width: '100%',
                      padding: '12px',
                      border: '1px solid #d1d5db',
                      borderRadius: '8px',
                      fontSize: '14px',
                      fontFamily: 'monospace',
                      resize: 'vertical',
                      marginBottom: '8px'
                    }}
                  />
                  <div style={{ fontSize: '12px', color: '#6b7280', lineHeight: '1.4', marginTop: '6px' }}>
                    Mỗi domain một dòng hoặc ngăn cách bằng dấu phẩy. Yêu cầu header X-Domain khi gọi API
                  </div>
                </div>
              </div>
              
              <div style={{
                backgroundColor: '#f0f9ff',
                border: '1px solid #0ea5e9',
                borderRadius: '8px',
                padding: '16px',
                marginTop: '20px'
              }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '8px' }}>
                  <span style={{ fontSize: '16px' }}>💡</span>
                  <span style={{ fontSize: '14px', fontWeight: '500', color: '#0369a1' }}>
                    Các loại xác thực:
                  </span>
                </div>
                <ul style={{ fontSize: '12px', color: '#0c4a6e', margin: 0, paddingLeft: '20px' }}>
                  <li><strong>Chỉ API Key:</strong> Không cần Device ID và Domain</li>
                  <li><strong>API Key + Device ID:</strong> Yêu cầu X-Device-ID header</li>
                  <li><strong>API Key + Domain:</strong> Yêu cầu X-Domain header</li>
                  <li><strong>Đầy đủ:</strong> Yêu cầu cả X-Device-ID và X-Domain</li>
                </ul>
              </div>
            </div>
            )}

            {formData.keyType === 'admin' && (
              <div style={{
                backgroundColor: '#fef3c7',
                border: '1px solid #f59e0b',
                borderRadius: '8px',
                padding: '12px',
                marginBottom: '16px'
              }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                  <span style={{ fontSize: '16px' }}>⚠️</span>
                  <span style={{ fontSize: '14px', fontWeight: '500', color: '#92400e' }}>
                    Admin Key - Toàn quyền
                  </span>
                </div>
                <div style={{ fontSize: '12px', color: '#92400e', marginTop: '4px' }}>
                  • Tất cả quyền truy cập • Không giới hạn thời gian • Rate limit cao
                </div>
              </div>
            )}

            <div style={{
              display: 'flex',
              gap: '12px',
              justifyContent: 'flex-end'
            }}>
              <button
                type="button"
                onClick={() => {
                  setShowCreateModal(false);
                  resetForm();
                }}
                style={{
                  padding: '10px 20px',
                  backgroundColor: 'white',
                  color: '#6b7280',
                  border: '1px solid #d1d5db',
                  borderRadius: '8px',
                  fontSize: '14px',
                  cursor: 'pointer'
                }}
              >
                Hủy
              </button>
              <button
                type="submit"
                style={{
                  padding: '10px 20px',
                  backgroundColor: '#2563eb',
                  color: 'white',
                  border: 'none',
                  borderRadius: '8px',
                  fontSize: '14px',
                  cursor: 'pointer'
                }}
              >
                {editingKey ? 'Cập nhật' : 'Tạo Key'}
              </button>
            </div>
          </form>
        </Modal>

        {/* New API Key Display Modal */}
        {newApiKey && (
          <Modal
            isOpen={!!newApiKey}
            onClose={() => setNewApiKey('')}
            title="API Key đã được tạo"
            size="md"
          >
            <div style={{
              backgroundColor: '#fef3c7',
              border: '1px solid #f59e0b',
              borderRadius: '8px',
              padding: '16px',
              marginBottom: '16px'
            }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '8px' }}>
                <span style={{ fontSize: '16px' }}>⚠️</span>
                <span style={{ fontSize: '14px', fontWeight: '600', color: '#92400e' }}>
                  Quan trọng!
                </span>
              </div>
              <p style={{ fontSize: '13px', color: '#92400e', margin: 0 }}>
                Đây là lần duy nhất bạn có thể xem API key này. Hãy copy và lưu trữ an toàn.
              </p>
            </div>

            <div style={{
              backgroundColor: '#f8fafc',
              border: '1px solid #e2e8f0',
              borderRadius: '8px',
              padding: '12px',
              marginBottom: '16px'
            }}>
              <label style={{
                display: 'block',
                fontSize: '12px',
                fontWeight: '600',
                color: '#64748b',
                marginBottom: '4px'
              }}>
                API Key:
              </label>
              <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                <code style={{
                  flex: 1,
                  padding: '8px',
                  backgroundColor: 'white',
                  border: '1px solid #d1d5db',
                  borderRadius: '4px',
                  fontSize: '12px',
                  fontFamily: 'monospace',
                  wordBreak: 'break-all'
                }}>
                  {newApiKey}
                </code>
                <button
                  onClick={() => copyToClipboard(newApiKey)}
                  style={{
                    padding: '6px 12px',
                    backgroundColor: '#2563eb',
                    color: 'white',
                    border: 'none',
                    borderRadius: '4px',
                    fontSize: '12px',
                    cursor: 'pointer'
                  }}
                >
                  📋 Copy
                </button>
              </div>
            </div>

            <div style={{ textAlign: 'center' }}>
              <button
                onClick={() => setNewApiKey('')}
                style={{
                  padding: '10px 20px',
                  backgroundColor: '#2563eb',
                  color: 'white',
                  border: 'none',
                  borderRadius: '8px',
                  fontSize: '14px',
                  cursor: 'pointer'
                }}
              >
                Đã lưu, đóng
              </button>
            </div>
          </Modal>
        )}

        {/* Profiles Management Modal */}
        {showProfilesModal && selectedKeyForProfiles && (
          <Modal
            isOpen={showProfilesModal}
            onClose={() => {
              setShowProfilesModal(false);
              setSelectedKeyForProfiles(null);
              setProfilesData(null);
            }}
            title={`Quản lý Profiles - ${selectedKeyForProfiles.keyName}`}
            size="xl"
          >
            {loadingProfiles ? (
              <div style={{ textAlign: 'center', padding: '40px' }}>
                <div style={{ fontSize: '16px', color: '#6b7280' }}>Đang tải...</div>
              </div>
            ) : profilesData ? (
              <div>
                {/* Stats Summary */}
                <div style={{
                  display: 'grid',
                  gridTemplateColumns: 'repeat(4, 1fr)',
                  gap: '16px',
                  marginBottom: '24px',
                  padding: '16px',
                  backgroundColor: '#f8fafc',
                  borderRadius: '8px'
                }}>
                  <div style={{ textAlign: 'center' }}>
                    <div style={{ fontSize: '24px', fontWeight: 'bold', color: '#1f2937' }}>
                      {profilesData.stats.total}
                    </div>
                    <div style={{ fontSize: '12px', color: '#6b7280' }}>Tổng Profiles</div>
                  </div>
                  <div style={{ textAlign: 'center' }}>
                    <div style={{ fontSize: '24px', fontWeight: 'bold', color: '#16a34a' }}>
                      {profilesData.stats.live}
                    </div>
                    <div style={{ fontSize: '12px', color: '#6b7280' }}>Live</div>
                  </div>
                  <div style={{ textAlign: 'center' }}>
                    <div style={{ fontSize: '24px', fontWeight: 'bold', color: '#dc2626' }}>
                      {profilesData.stats.dead}
                    </div>
                    <div style={{ fontSize: '12px', color: '#6b7280' }}>Dead</div>
                  </div>
                  <div style={{ textAlign: 'center' }}>
                    <div style={{ fontSize: '24px', fontWeight: 'bold', color: '#2563eb' }}>
                      {profilesData.maxProfiles === -1 ? 'No limit' : profilesData.stats.available}
                    </div>
                    <div style={{ fontSize: '12px', color: '#6b7280' }}>Còn trống</div>
                  </div>
                </div>

                {/* Profiles Table */}
                {profilesData.profiles.length > 0 ? (
                  <div style={{ overflowX: 'auto' }}>
                    <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                      <thead>
                        <tr style={{ backgroundColor: '#f1f5f9' }}>
                          <th style={{ padding: '12px', textAlign: 'left', fontSize: '12px', fontWeight: '600', color: '#475569' }}>
                            Số điện thoại
                          </th>
                          <th style={{ padding: '12px', textAlign: 'left', fontSize: '12px', fontWeight: '600', color: '#475569' }}>
                            Session ID
                          </th>
                          <th style={{ padding: '12px', textAlign: 'left', fontSize: '12px', fontWeight: '600', color: '#475569' }}>
                            Proxy
                          </th>
                          <th style={{ padding: '12px', textAlign: 'left', fontSize: '12px', fontWeight: '600', color: '#475569' }}>
                            Trạng thái
                          </th>
                          <th style={{ padding: '12px', textAlign: 'left', fontSize: '12px', fontWeight: '600', color: '#475569' }}>
                            Login lúc
                          </th>
                          <th style={{ padding: '12px', textAlign: 'center', fontSize: '12px', fontWeight: '600', color: '#475569' }}>
                            Thao tác
                          </th>
                        </tr>
                      </thead>
                      <tbody>
                        {profilesData.profiles.map((profile) => (
                          <tr key={profile.sessionId} style={{ borderBottom: '1px solid #e2e8f0' }}>
                            <td style={{ padding: '12px', fontSize: '14px', fontWeight: '500' }}>
                              {profile.phoneNumber}
                            </td>
                            <td style={{ padding: '12px', fontSize: '12px', color: '#6b7280', fontFamily: 'monospace' }}>
                              <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                                <span>{profile.sessionId.substring(0, 20)}...</span>
                                <button
                                  onClick={() => {
                                    navigator.clipboard.writeText(profile.sessionId);
                                    setNotification({
                                      show: true,
                                      type: 'success',
                                      message: 'Đã copy Session ID!'
                                    });
                                    setTimeout(() => setNotification(prev => ({ ...prev, show: false })), 2000);
                                  }}
                                  style={{
                                    padding: '2px 4px',
                                    backgroundColor: '#f1f5f9',
                                    border: '1px solid #cbd5e1',
                                    borderRadius: '4px',
                                    fontSize: '10px',
                                    cursor: 'pointer',
                                    color: '#475569'
                                  }}
                                  title="Copy Session ID"
                                >
                                  📋
                                </button>
                                <button
                                  onClick={() => {
                                    alert(`Full Session ID:\n${profile.sessionId}\n\nPhone: ${profile.phoneNumber}\nProxy: ${profile.proxy || 'Không có'}\nLogin: ${new Date(profile.loginAt).toLocaleString('vi-VN')}\nStatus: ${profile.isLive ? 'Live' : 'Dead'}`);
                                  }}
                                  style={{
                                    padding: '2px 4px',
                                    backgroundColor: '#f0f9ff',
                                    border: '1px solid #0ea5e9',
                                    borderRadius: '4px',
                                    fontSize: '10px',
                                    cursor: 'pointer',
                                    color: '#0369a1'
                                  }}
                                  title="Show Full Info"
                                >
                                  👁️
                                </button>
                              </div>
                            </td>
                            <td style={{ padding: '12px', fontSize: '12px', color: '#6b7280' }}>
                              {profile.proxy ? (
                                <div style={{
                                  padding: '4px 8px',
                                  backgroundColor: '#f0fdf4',
                                  border: '1px solid #22c55e',
                                  borderRadius: '6px',
                                  fontSize: '11px',
                                  color: '#15803d',
                                  fontFamily: 'monospace',
                                  maxWidth: '120px',
                                  overflow: 'hidden',
                                  textOverflow: 'ellipsis',
                                  whiteSpace: 'nowrap'
                                }}>
                                  🌐 {profile.proxy}
                                </div>
                              ) : (
                                <span style={{ color: '#9ca3af', fontSize: '11px' }}>Không có</span>
                              )}
                            </td>
                            <td style={{ padding: '12px' }}>
                              <span style={{
                                padding: '4px 8px',
                                borderRadius: '12px',
                                fontSize: '12px',
                                fontWeight: '500',
                                backgroundColor: profile.isLive ? '#dcfce7' : '#fecaca',
                                color: profile.isLive ? '#16a34a' : '#dc2626'
                              }}>
                                {profile.isLive ? '🟢 Live' : '🔴 Dead'}
                              </span>
                            </td>
                            <td style={{ padding: '12px', fontSize: '13px', color: '#64748b' }}>
                              {new Date(profile.loginAt).toLocaleString('vi-VN')}
                            </td>
                            <td style={{ padding: '12px', textAlign: 'center' }}>
                              <div style={{ display: 'flex', gap: '4px', justifyContent: 'center' }}>
                                {hasPermission('whatsapp', 'update') && (
                                  <button
                                    onClick={() => handleUpdateProfileLive(profile.sessionId, !profile.isLive)}
                                    style={{
                                      padding: '4px 8px',
                                      backgroundColor: profile.isLive ? '#fecaca' : '#dcfce7',
                                      border: `1px solid ${profile.isLive ? '#f87171' : '#86efac'}`,
                                      borderRadius: '4px',
                                      fontSize: '12px',
                                      color: profile.isLive ? '#dc2626' : '#16a34a',
                                      cursor: 'pointer'
                                    }}
                                    title={profile.isLive ? 'Đánh dấu Dead' : 'Đánh dấu Live'}
                                  >
                                    {profile.isLive ? '❌' : '✅'}
                                  </button>
                                )}
                                {hasPermission('whatsapp', 'delete') && (
                                  <button
                                    onClick={() => handleDeleteProfile(profile.sessionId, profile.phoneNumber)}
                                    style={{
                                      padding: '4px 8px',
                                      backgroundColor: '#fee2e2',
                                      border: '1px solid #fca5a5',
                                      borderRadius: '4px',
                                      fontSize: '12px',
                                      color: '#dc2626',
                                      cursor: 'pointer'
                                    }}
                                    title="Xóa Profile"
                                  >
                                    🗑️
                                  </button>
                                )}
                              </div>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                ) : (
                  <div style={{
                    textAlign: 'center',
                    padding: '40px',
                    color: '#6b7280',
                    backgroundColor: '#f8fafc',
                    borderRadius: '8px'
                  }}>
                    <div style={{ fontSize: '48px', marginBottom: '16px' }}>📱</div>
                    <div style={{ fontSize: '16px', fontWeight: '500', marginBottom: '8px' }}>
                      Chưa có profiles nào
                    </div>
                    <div style={{ fontSize: '14px' }}>
                      Sử dụng API login để tạo profiles mới
                    </div>
                  </div>
                )}
              </div>
            ) : (
              <div style={{ textAlign: 'center', padding: '40px' }}>
                <div style={{ fontSize: '16px', color: '#dc2626' }}>Không thể tải dữ liệu</div>
              </div>
            )}
          </Modal>
        )}
      </div>
    </Layout>
  );
};

export default WhatsAppKeys;
